import React, { useEffect, useState } from 'react';
import { FileText, Shield, ClipboardList, Lock, Eye, CheckCircle, AlertTriangle, Loader2, ArrowRight, Download, ChevronLeft, Sparkles, ExternalLink, Check } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { Button } from '../common/Button';
interface GovernanceEvidenceAgentProps {
  onNavigate?: (dashboard: string) => void;
}
type CategoryId = 'board-reporting' | 'regulatory-filings' | 'audit-trail' | 'ring-fencing' | 'outsourcing';
interface EvidenceItem {
  id: string;
  title: string;
  description: string;
  source: string;
  sourceDashboard: string; // For navigation
  dataPreview: string;
  status: 'complete' | 'assembling' | 'incomplete';
}
interface Category {
  id: CategoryId;
  name: string;
  icon: React.ElementType;
  description: string;
}
const categories: Category[] = [{
  id: 'board-reporting',
  name: 'Board Reporting',
  icon: FileText,
  description: 'Quarterly performance and risk reports for the Board'
}, {
  id: 'regulatory-filings',
  name: 'Regulatory Filings',
  icon: Shield,
  description: 'Solvency II and local regulatory submission evidence'
}, {
  id: 'audit-trail',
  name: 'Audit Trail',
  icon: ClipboardList,
  description: 'System logs, user access, and change management records'
}, {
  id: 'ring-fencing',
  name: 'Ring-Fencing Evidence',
  icon: Lock,
  description: 'Proof of capital and operational segregation between cells'
}, {
  id: 'outsourcing',
  name: 'Outsourcing Oversight',
  icon: Eye,
  description: 'Monitoring of MGAs, TPAs, and other service providers'
}];
const evidenceData: Record<CategoryId, EvidenceItem[]> = {
  'board-reporting': [{
    id: 'br-1',
    title: 'Portfolio Performance Summary',
    description: 'High-level GWP and Loss Ratio metrics across all cells',
    source: 'Portfolio Overview',
    sourceDashboard: 'Portfolio Overview',
    dataPreview: 'Net LR: 58.4% | GWP: R45.2M',
    status: 'complete'
  }, {
    id: 'br-2',
    title: 'Cell-by-Cell Results',
    description: 'Individual cell profitability and capital status',
    source: 'Cell Performance',
    sourceDashboard: 'Cell Performance',
    dataPreview: '8 cells | 6 profitable',
    status: 'complete'
  }, {
    id: 'br-3',
    title: 'Capital Position Statement',
    description: 'Current solvency and capital buffer status',
    source: 'Capital & Reinsurance',
    sourceDashboard: 'Capital & Reinsurance',
    dataPreview: 'SCR: 178% | Buffer: R6.7M',
    status: 'complete'
  }, {
    id: 'br-4',
    title: 'Claims Activity Report',
    description: 'Summary of open claims and large loss movements',
    source: 'Claims & Governance',
    sourceDashboard: 'Claims & Governance',
    dataPreview: '142 open | 3 large losses',
    status: 'complete'
  }],
  'regulatory-filings': [{
    id: 'rf-1',
    title: 'Solvency Capital Requirement',
    description: 'Detailed SCR calculation and coverage ratio',
    source: 'Capital & Reinsurance',
    sourceDashboard: 'Capital & Reinsurance',
    dataPreview: 'SCR Coverage: 178%',
    status: 'complete'
  }, {
    id: 'rf-2',
    title: 'Minimum Capital Requirement',
    description: 'MCR calculation and compliance status',
    source: 'Capital & Reinsurance',
    sourceDashboard: 'Capital & Reinsurance',
    dataPreview: 'MCR: R3.2M',
    status: 'complete'
  }, {
    id: 'rf-3',
    title: 'Quarterly Regulatory Return',
    description: 'Aggregated data for QRT submission',
    source: 'Portfolio Overview',
    sourceDashboard: 'Portfolio Overview',
    dataPreview: 'All cells compliant',
    status: 'complete'
  }, {
    id: 'rf-4',
    title: 'Stress Test Results',
    description: 'Impact analysis of standard stress scenarios',
    source: 'Capital & Reinsurance',
    sourceDashboard: 'Capital & Reinsurance',
    dataPreview: '3 scenarios passed',
    status: 'complete'
  }],
  'audit-trail': [{
    id: 'at-1',
    title: 'Data Change Log',
    description: 'Record of all data modifications in the period',
    source: 'System',
    sourceDashboard: 'Portfolio Overview',
    dataPreview: '847 changes this quarter',
    status: 'complete'
  }, {
    id: 'at-2',
    title: 'User Access Records',
    description: 'Active users and permission levels',
    source: 'System',
    sourceDashboard: 'Portfolio Overview',
    dataPreview: '14 active users',
    status: 'complete'
  }, {
    id: 'at-3',
    title: 'Approval Workflow History',
    description: 'Log of all underwriting and claims approvals',
    source: 'System',
    sourceDashboard: 'Portfolio Overview',
    dataPreview: '23 approvals processed',
    status: 'complete'
  }, {
    id: 'at-4',
    title: 'Exception Handling Log',
    description: 'Record of authority breaches and resolutions',
    source: 'Underwriting & Pricing',
    sourceDashboard: 'Underwriting & Pricing',
    dataPreview: '8 exceptions reviewed',
    status: 'complete'
  }],
  'ring-fencing': [{
    id: 'rng-1',
    title: 'Cell Capital Isolation',
    description: 'Evidence of separate capital accounts per cell',
    source: 'Cell Performance',
    sourceDashboard: 'Cell Performance',
    dataPreview: '8 cells independently capitalised',
    status: 'complete'
  }, {
    id: 'rng-2',
    title: 'Separate Claims Handling',
    description: 'Confirmation of segregated claims processes',
    source: 'Claims & Governance',
    sourceDashboard: 'Claims & Governance',
    dataPreview: 'Per-cell claims segregation confirmed',
    status: 'complete'
  }, {
    id: 'rng-3',
    title: 'Independent Accounting',
    description: 'Separate P&L and balance sheets per cell',
    source: 'Cell Performance',
    sourceDashboard: 'Cell Performance',
    dataPreview: '8 separate P&L statements',
    status: 'complete'
  }, {
    id: 'rng-4',
    title: 'Reinsurance Segregation',
    description: 'Evidence of cell-specific reinsurance contracts',
    source: 'Capital & Reinsurance',
    sourceDashboard: 'Capital & Reinsurance',
    dataPreview: 'Cell-specific treaties in place',
    status: 'complete'
  }],
  outsourcing: [{
    id: 'out-1',
    title: 'MGA Performance Review',
    description: 'Underwriting performance vs business plan',
    source: 'Underwriting & Pricing',
    sourceDashboard: 'Underwriting & Pricing',
    dataPreview: 'Rate adequacy: 1.12',
    status: 'complete'
  }, {
    id: 'out-2',
    title: 'SLA Compliance Report',
    description: 'Claims handling timeliness against SLAs',
    source: 'Claims & Governance',
    sourceDashboard: 'Claims & Governance',
    dataPreview: '48h ack: 98.5%',
    status: 'complete'
  }, {
    id: 'out-3',
    title: 'Authority Utilisation',
    description: 'Monitoring of delegated authority limits',
    source: 'Underwriting & Pricing',
    sourceDashboard: 'Underwriting & Pricing',
    dataPreview: '3 breaches this quarter',
    status: 'complete'
  }, {
    id: 'out-4',
    title: 'Delegated Authority Audit',
    description: 'Compliance with binder agreements',
    source: 'Underwriting & Pricing',
    sourceDashboard: 'Underwriting & Pricing',
    dataPreview: 'All binders within limits',
    status: 'complete'
  }]
};
export function GovernanceEvidenceAgent({
  onNavigate
}: GovernanceEvidenceAgentProps) {
  const [selectedCategory, setSelectedCategory] = useState<CategoryId>('board-reporting');
  const [assemblingItems, setAssemblingItems] = useState<Set<string>>(new Set());
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [isCompilingAll, setIsCompilingAll] = useState(false);
  // Simulate assembly when category changes
  useEffect(() => {
    if (!showReportPreview) {
      triggerAssembly(evidenceData[selectedCategory].map(i => i.id));
    }
  }, [selectedCategory, showReportPreview]);
  const triggerAssembly = (itemIds: string[]) => {
    // Set all to assembling
    setAssemblingItems(prev => {
      const next = new Set(prev);
      itemIds.forEach(id => next.add(id));
      return next;
    });
    // Clear them one by one with random delays
    itemIds.forEach(id => {
      const delay = 500 + Math.random() * 1000;
      setTimeout(() => {
        setAssemblingItems(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, delay);
    });
  };
  const handleCompileAll = () => {
    setIsCompilingAll(true);
    const allIds = Object.values(evidenceData).flat().map(i => i.id);
    triggerAssembly(allIds);
    // After max delay, show report
    setTimeout(() => {
      setIsCompilingAll(false);
      setShowReportPreview(true);
    }, 2000);
  };
  const currentItems = evidenceData[selectedCategory];
  if (showReportPreview) {
    const logoUrl = 'https://cdn.magicpatterns.com/uploads/gXiKzQzKkKrG1uR8895vbZ/Accrue_logo_v2_(1)_(1).png';
    // Mock Data for Charts
    const gwpData = [{
      name: 'Q1',
      value: 10.2
    }, {
      name: 'Q2',
      value: 12.1
    }, {
      name: 'Q3',
      value: 11.8
    }, {
      name: 'Q4',
      value: 11.1
    }];
    const cellProfitabilityData = [{
      name: 'Profitable',
      value: 6,
      color: '#22C55E'
    }, {
      name: 'Underperforming',
      value: 2,
      color: '#EF4444'
    }];
    const capitalData = [{
      name: 'Covered',
      value: 178,
      color: '#22C55E'
    }, {
      name: 'Remaining',
      value: 72,
      color: '#E5E5E5'
    }];
    const claimsData = [{
      name: 'Open',
      value: 142
    }, {
      name: 'Large',
      value: 3
    }, {
      name: 'Litigated',
      value: 12
    }];
    const solvencyData = [{
      name: 'Available',
      value: 15.2,
      fill: '#22C55E'
    }, {
      name: 'SCR',
      value: 8.5,
      fill: '#EC4899'
    }, {
      name: 'MCR',
      value: 3.2,
      fill: '#9333EA'
    }];
    const ringFencingCells = ['Ubuntu Fleet', 'Highveld Logistics', 'Imbokodo Mobility', 'Baobab Transport', 'Karoo Property', 'Drakensberg Asset', 'Mzansi Business', 'Umhlanga Retail'];
    return <div className="bg-neutral-100 min-h-screen flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-626">
        {/* Sticky Header */}
        <div className="sticky top-0 z-50 bg-neutral-900 text-white p-4 flex justify-between items-center shadow-md" data-id="element-627">
          <div data-id="element-628">
            <h2 className="text-lg font-bold" data-id="element-629">Governance Evidence Pack</h2>
            <p className="text-neutral-400 text-xs" data-id="element-630">
              Generated on {new Date().toLocaleDateString()}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-631">
            <Button variant="outline" className="border-neutral-700 text-neutral-300 hover:bg-neutral-800 hover:text-white text-sm py-1" onClick={() => setShowReportPreview(false)} data-id="element-632">
              <ChevronLeft size={14} data-id="element-633" /> Back to Evidence
            </Button>
            <Button className="bg-pink-600 hover:bg-pink-700 text-white border-none text-sm py-1" data-id="element-634">
              <Download size={14} data-id="element-635" /> Export PDF
            </Button>
          </div>
        </div>

        {/* Slides Container */}
        <div className="flex-1 overflow-y-auto p-8 space-y-8 flex flex-col items-center" data-id="element-636">
          {/* Slide 1: Title Slide */}
          <div className="w-full max-w-5xl aspect-video bg-neutral-900 text-white rounded-xl shadow-2xl p-16 relative flex flex-col justify-center overflow-hidden" data-id="element-637">
            <img src={logoUrl} alt="Accrue Logo" className="absolute top-12 right-12 w-32 brightness-0 invert" data-id="element-638" />
            <div className="space-y-6 z-10" data-id="element-639">
              <div className="w-24 h-1 bg-pink-500 mb-8" data-id="element-640" />
              <h1 className="text-6xl font-bold tracking-tight text-white" data-id="element-641">
                Governance
                <br data-id="element-642" />
                Evidence Pack
              </h1>
              <p className="text-2xl text-neutral-400 font-light" data-id="element-643">
                Euroguard Cell Captive — Q3 2024
              </p>
            </div>
            <div className="absolute bottom-12 left-16 text-neutral-500 text-sm" data-id="element-644">
              <p data-id="element-645">Compiled by Governance Agent</p>
              <p data-id="element-646">{new Date().toLocaleDateString()}</p>
            </div>
            {/* Abstract background element */}
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-neutral-800 rounded-full opacity-50 blur-3xl" data-id="element-647" />
          </div>

          {/* Slide 2: Board Reporting */}
          <div className="w-full max-w-5xl aspect-video bg-white rounded-xl shadow-xl relative overflow-hidden flex flex-col border-t-8 border-pink-500" data-id="element-648">
            <div className="p-10 flex-1 flex flex-col" data-id="element-649">
              <div className="flex justify-between items-start mb-8" data-id="element-650">
                <div className="flex items-center gap-3" data-id="element-651">
                  <div className="p-3 bg-pink-50 rounded-lg" data-id="element-652">
                    <FileText size={32} className="text-pink-600" data-id="element-653" />
                  </div>
                  <h2 className="text-3xl font-bold text-neutral-900" data-id="element-654">
                    Board Reporting
                  </h2>
                </div>
                <img src={logoUrl} alt="Accrue" className="w-20" data-id="element-655" />
              </div>

              <div className="grid grid-cols-2 gap-8 flex-1" data-id="element-656">
                {/* KPI 1: Portfolio Performance */}
                <div className="bg-neutral-50 p-6 rounded-lg border border-neutral-100 flex flex-col" data-id="element-657">
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase mb-4" data-id="element-658">
                    Portfolio GWP (Quarterly)
                  </h3>
                  <div className="flex-1 min-h-0" data-id="element-659">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-660">
                      <BarChart data={gwpData} data-id="element-661">
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                        fontSize: 12
                      }} data-id="element-662" />
                        <Tooltip cursor={{
                        fill: 'transparent'
                      }} contentStyle={{
                        borderRadius: '8px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }} data-id="element-663" />
                        <Bar dataKey="value" fill="#EC4899" radius={[4, 4, 0, 0]} data-id="element-664" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* KPI 2: Cell Results */}
                <div className="bg-neutral-50 p-6 rounded-lg border border-neutral-100 flex flex-col" data-id="element-665">
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase mb-4" data-id="element-666">
                    Cell Profitability Status
                  </h3>
                  <div className="flex-1 min-h-0 flex items-center justify-center gap-8" data-id="element-667">
                    <div className="w-32 h-32" data-id="element-668">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-669">
                        <PieChart data-id="element-670">
                          <Pie data={cellProfitabilityData} innerRadius={25} outerRadius={40} paddingAngle={5} dataKey="value" data-id="element-671">
                            {cellProfitabilityData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-672" />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2" data-id="element-673">
                      <div className="flex items-center gap-2" data-id="element-674">
                        <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-675" />
                        <span className="text-sm font-medium" data-id="element-676">
                          6 Profitable
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-677">
                        <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-678" />
                        <span className="text-sm font-medium" data-id="element-679">
                          2 Underperforming
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* KPI 3: Capital Position */}
                <div className="bg-neutral-50 p-6 rounded-lg border border-neutral-100 flex flex-col" data-id="element-680">
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase mb-4" data-id="element-681">
                    SCR Coverage Ratio
                  </h3>
                  <div className="flex-1 flex items-center justify-center relative" data-id="element-682">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-683">
                      <PieChart data-id="element-684">
                        <Pie data={capitalData} startAngle={180} endAngle={0} innerRadius={60} outerRadius={80} dataKey="value" data-id="element-685">
                          {capitalData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-686" />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute bottom-4 text-center" data-id="element-687">
                      <span className="text-4xl font-bold text-neutral-900" data-id="element-688">
                        178%
                      </span>
                      <p className="text-xs text-neutral-500" data-id="element-689">
                        Target: &gt;150%
                      </p>
                    </div>
                  </div>
                </div>

                {/* KPI 4: Claims Activity */}
                <div className="bg-neutral-50 p-6 rounded-lg border border-neutral-100 flex flex-col" data-id="element-690">
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase mb-4" data-id="element-691">
                    Claims Activity
                  </h3>
                  <div className="flex-1 min-h-0" data-id="element-692">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-693">
                      <BarChart layout="vertical" data={claimsData} margin={{
                      left: 20
                    }} data-id="element-694">
                        <XAxis type="number" hide data-id="element-695" />
                        <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{
                        fontSize: 12
                      }} data-id="element-696" />
                        <Bar dataKey="value" fill="#9333EA" radius={[0, 4, 4, 0]} barSize={20} data-id="element-697" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-4 right-6 text-neutral-300 text-xs font-mono" data-id="element-698">
              02
            </div>
          </div>

          {/* Slide 3: Regulatory Filings */}
          <div className="w-full max-w-5xl aspect-video bg-white rounded-xl shadow-xl relative overflow-hidden flex flex-col border-t-8 border-pink-500" data-id="element-699">
            <div className="p-10 flex-1 flex flex-col" data-id="element-700">
              <div className="flex justify-between items-start mb-8" data-id="element-701">
                <div className="flex items-center gap-3" data-id="element-702">
                  <div className="p-3 bg-pink-50 rounded-lg" data-id="element-703">
                    <Shield size={32} className="text-pink-600" data-id="element-704" />
                  </div>
                  <h2 className="text-3xl font-bold text-neutral-900" data-id="element-705">
                    Regulatory Filings
                  </h2>
                </div>
                <img src={logoUrl} alt="Accrue" className="w-20" data-id="element-706" />
              </div>

              <div className="grid grid-cols-2 gap-12 flex-1" data-id="element-707">
                {/* Left: Solvency Waterfall */}
                <div className="flex flex-col" data-id="element-708">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-6" data-id="element-709">
                    Solvency Position (R millions)
                  </h3>
                  <div className="flex-1 bg-neutral-50 rounded-lg p-6 border border-neutral-100" data-id="element-710">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-711">
                      <BarChart data={solvencyData} barSize={60} data-id="element-712">
                        <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-713" />
                        <YAxis hide data-id="element-714" />
                        <Tooltip cursor={{
                        fill: 'transparent'
                      }} contentStyle={{
                        borderRadius: '8px'
                      }} data-id="element-715" />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} data-id="element-716">
                          {solvencyData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-717" />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Right: Stress Tests */}
                <div className="flex flex-col" data-id="element-718">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-6" data-id="element-719">
                    Stress Test Scenarios
                  </h3>
                  <div className="space-y-6" data-id="element-720">
                    {[{
                    name: '1-in-100 Wind Event',
                    status: 'Passed'
                  }, {
                    name: '1-in-250 Earthquake',
                    status: 'Passed'
                  }, {
                    name: 'Inflation Shock (+10%)',
                    status: 'Passed'
                  }].map((test, idx) => <div key={idx} className="space-y-2" data-id="element-721">
                        <div className="flex justify-between text-sm font-medium" data-id="element-722">
                          <span data-id="element-723">{test.name}</span>
                          <span className="text-green-600" data-id="element-724">{test.status}</span>
                        </div>
                        <div className="h-3 bg-neutral-100 rounded-full overflow-hidden" data-id="element-725">
                          <div className="h-full w-full bg-green-500 rounded-full" data-id="element-726" />
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-100" data-id="element-727">
                    <h4 className="font-semibold text-blue-900 mb-2" data-id="element-728">
                      Agent Analysis
                    </h4>
                    <p className="text-sm text-blue-800 leading-relaxed" data-id="element-729">
                      Capital buffers remain sufficient across all modelled
                      scenarios. The portfolio shows highest sensitivity to
                      inflation shocks, but remains within 120% SCR tolerance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-4 right-6 text-neutral-300 text-xs font-mono" data-id="element-730">
              03
            </div>
          </div>

          {/* Slide 4: Audit Trail */}
          <div className="w-full max-w-5xl aspect-video bg-white rounded-xl shadow-xl relative overflow-hidden flex flex-col border-t-8 border-pink-500" data-id="element-731">
            <div className="p-10 flex-1 flex flex-col" data-id="element-732">
              <div className="flex justify-between items-start mb-8" data-id="element-733">
                <div className="flex items-center gap-3" data-id="element-734">
                  <div className="p-3 bg-pink-50 rounded-lg" data-id="element-735">
                    <ClipboardList size={32} className="text-pink-600" data-id="element-736" />
                  </div>
                  <h2 className="text-3xl font-bold text-neutral-900" data-id="element-737">
                    Audit Trail
                  </h2>
                </div>
                <img src={logoUrl} alt="Accrue" className="w-20" data-id="element-738" />
              </div>

              {/* Metrics Row */}
              <div className="grid grid-cols-4 gap-6 mb-12" data-id="element-739">
                {[{
                label: 'Data Changes',
                value: '847',
                color: 'text-blue-600'
              }, {
                label: 'Active Users',
                value: '14',
                color: 'text-purple-600'
              }, {
                label: 'Approvals',
                value: '23',
                color: 'text-green-600'
              }, {
                label: 'Exceptions',
                value: '8',
                color: 'text-amber-600'
              }].map(metric => <div key={metric.label} className="p-6 bg-neutral-50 rounded-lg border border-neutral-100 text-center" data-id="element-740">
                    <div className={`text-4xl font-bold mb-2 ${metric.color}`} data-id="element-741">
                      {metric.value}
                    </div>
                    <div className="text-sm text-neutral-500 font-medium uppercase tracking-wide" data-id="element-742">
                      {metric.label}
                    </div>
                  </div>)}
              </div>

              {/* Timeline */}
              <div className="flex-1 bg-white rounded-lg border border-neutral-200 p-6" data-id="element-743">
                <h3 className="text-lg font-semibold text-neutral-900 mb-6" data-id="element-744">
                  Recent System Activity
                </h3>
                <div className="space-y-6 relative before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-neutral-200" data-id="element-745">
                  {[{
                  time: '14:32 Today',
                  user: 'Sarah Jenkins',
                  action: 'Approved large loss reserve update (CLM-892)'
                }, {
                  time: '11:15 Today',
                  user: 'System',
                  action: 'Automated bordereau ingestion completed'
                }, {
                  time: '09:45 Today',
                  user: 'Mike Ross',
                  action: 'Updated reinsurance treaty limits'
                }, {
                  time: '16:20 Yesterday',
                  user: 'Sarah Jenkins',
                  action: 'Generated monthly performance report'
                }, {
                  time: '14:10 Yesterday',
                  user: 'Admin',
                  action: 'Added new user (J. Doe) to Underwriting group'
                }].map((log, idx) => <div key={idx} className="flex gap-4 items-start relative" data-id="element-746">
                      <div className="w-4 h-4 rounded-full bg-neutral-400 border-2 border-white shadow-sm mt-1 z-10" data-id="element-747" />
                      <div className="flex-1 flex justify-between items-start p-3 bg-neutral-50 rounded border border-neutral-100" data-id="element-748">
                        <div data-id="element-749">
                          <p className="text-sm font-medium text-neutral-900" data-id="element-750">
                            {log.action}
                          </p>
                          <p className="text-xs text-neutral-500 mt-1" data-id="element-751">
                            User: {log.user}
                          </p>
                        </div>
                        <span className="text-xs font-mono text-neutral-400" data-id="element-752">
                          {log.time}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
            <div className="absolute bottom-4 right-6 text-neutral-300 text-xs font-mono" data-id="element-753">
              04
            </div>
          </div>

          {/* Slide 5: Ring-Fencing Evidence */}
          <div className="w-full max-w-5xl aspect-video bg-white rounded-xl shadow-xl relative overflow-hidden flex flex-col border-t-8 border-pink-500" data-id="element-754">
            <div className="p-10 flex-1 flex flex-col" data-id="element-755">
              <div className="flex justify-between items-start mb-8" data-id="element-756">
                <div className="flex items-center gap-3" data-id="element-757">
                  <div className="p-3 bg-pink-50 rounded-lg" data-id="element-758">
                    <Lock size={32} className="text-pink-600" data-id="element-759" />
                  </div>
                  <h2 className="text-3xl font-bold text-neutral-900" data-id="element-760">
                    Ring-Fencing Evidence
                  </h2>
                </div>
                <img src={logoUrl} alt="Accrue" className="w-20" data-id="element-761" />
              </div>

              <div className="grid grid-cols-4 gap-4 mb-8" data-id="element-762">
                {ringFencingCells.map(cell => <div key={cell} className="p-4 bg-neutral-50 rounded-lg border border-neutral-100 flex flex-col items-center text-center" data-id="element-763">
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-3" data-id="element-764">
                      <Check size={16} strokeWidth={3} data-id="element-765" />
                    </div>
                    <span className="text-sm font-medium text-neutral-900" data-id="element-766">
                      {cell}
                    </span>
                    <span className="text-xs text-green-600 mt-1 font-medium" data-id="element-767">
                      Isolated
                    </span>
                  </div>)}
              </div>

              <div className="space-y-3 mt-auto" data-id="element-768">
                {['Cell Capital Isolation confirmed via separate bank accounts', 'Separate Claims Handling processes verified', 'Independent Accounting ledgers for all 8 cells', 'Reinsurance Segregation via cell-specific treaties'].map((item, idx) => <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 rounded border border-green-100" data-id="element-769">
                    <CheckCircle size={18} className="text-green-600" data-id="element-770" />
                    <span className="text-sm text-green-900 font-medium" data-id="element-771">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
            <div className="absolute bottom-4 right-6 text-neutral-300 text-xs font-mono" data-id="element-772">
              05
            </div>
          </div>

          {/* Slide 6: Outsourcing Oversight */}
          <div className="w-full max-w-5xl aspect-video bg-white rounded-xl shadow-xl relative overflow-hidden flex flex-col border-t-8 border-pink-500" data-id="element-773">
            <div className="p-10 flex-1 flex flex-col" data-id="element-774">
              <div className="flex justify-between items-start mb-8" data-id="element-775">
                <div className="flex items-center gap-3" data-id="element-776">
                  <div className="p-3 bg-pink-50 rounded-lg" data-id="element-777">
                    <Eye size={32} className="text-pink-600" data-id="element-778" />
                  </div>
                  <h2 className="text-3xl font-bold text-neutral-900" data-id="element-779">
                    Outsourcing Oversight
                  </h2>
                </div>
                <img src={logoUrl} alt="Accrue" className="w-20" data-id="element-780" />
              </div>

              <div className="grid grid-cols-2 gap-12 flex-1" data-id="element-781">
                {/* Left: SLA Compliance */}
                <div className="flex flex-col justify-center space-y-8" data-id="element-782">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2" data-id="element-783">
                    SLA Compliance
                  </h3>
                  {[{
                  label: '48h Acknowledgement',
                  value: 98.5,
                  target: 95
                }, {
                  label: 'Coverage Decision',
                  value: 92.0,
                  target: 90
                }, {
                  label: 'Payment Timeliness',
                  value: 99.0,
                  target: 98
                }].map(sla => <div key={sla.label} data-id="element-784">
                      <div className="flex justify-between mb-2" data-id="element-785">
                        <span className="font-medium text-neutral-700" data-id="element-786">
                          {sla.label}
                        </span>
                        <span className="font-bold text-neutral-900" data-id="element-787">
                          {sla.value}%
                        </span>
                      </div>
                      <div className="h-4 bg-neutral-100 rounded-full overflow-hidden relative" data-id="element-788">
                        <div className="h-full bg-purple-600 rounded-full" style={{
                      width: `${sla.value}%`
                    }} data-id="element-789" />
                        <div className="absolute top-0 bottom-0 w-0.5 bg-black/20" style={{
                      left: `${sla.target}%`
                    }} data-id="element-790" />
                      </div>
                      <div className="flex justify-end mt-1" data-id="element-791">
                        <span className="text-xs text-neutral-400" data-id="element-792">
                          Target: {sla.target}%
                        </span>
                      </div>
                    </div>)}
                </div>

                {/* Right: Authority Summary */}
                <div className="bg-neutral-50 rounded-xl p-8 border border-neutral-100 flex flex-col justify-center" data-id="element-793">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-6" data-id="element-794">
                    Authority Utilisation
                  </h3>

                  <div className="grid grid-cols-2 gap-6" data-id="element-795">
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm" data-id="element-796">
                      <div className="text-3xl font-bold text-green-600 mb-1" data-id="element-797">
                        1.12
                      </div>
                      <div className="text-xs text-neutral-500 uppercase font-medium" data-id="element-798">
                        Rate Adequacy
                      </div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm" data-id="element-799">
                      <div className="text-3xl font-bold text-amber-500 mb-1" data-id="element-800">
                        3
                      </div>
                      <div className="text-xs text-neutral-500 uppercase font-medium" data-id="element-801">
                        Breaches
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-green-100 rounded-lg border border-green-200 flex items-start gap-3" data-id="element-802">
                    <CheckCircle size={20} className="text-green-700 mt-0.5 flex-shrink-0" data-id="element-803" />
                    <div data-id="element-804">
                      <p className="font-semibold text-green-800 text-sm" data-id="element-805">
                        All Binders Compliant
                      </p>
                      <p className="text-xs text-green-700 mt-1" data-id="element-806">
                        Annual audit completed 14 Oct 2024. No material
                        findings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-4 right-6 text-neutral-300 text-xs font-mono" data-id="element-807">
              06
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="flex h-[calc(100vh-140px)] bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden" data-id="element-808">
      {/* Left Sidebar */}
      <div className="w-64 bg-neutral-50 border-r border-neutral-200 flex flex-col" data-id="element-809">
        <div className="p-4 border-b border-neutral-200" data-id="element-810">
          <h2 className="text-lg font-bold text-neutral-900 flex items-center gap-2" data-id="element-811">
            <Sparkles size={18} className="text-pink-600" data-id="element-812" />
            Governance Agent
          </h2>
          <p className="text-xs text-neutral-500 mt-1" data-id="element-813">
            Automated evidence compilation
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-id="element-814">
          {categories.map(cat => <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`w-full text-left px-3 py-3 rounded-md flex items-center gap-3 transition-colors ${selectedCategory === cat.id ? 'bg-white text-pink-600 shadow-sm border border-neutral-200' : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'}`} data-id="element-815">
              <cat.icon size={18} className={selectedCategory === cat.id ? 'text-pink-500' : 'text-neutral-400'} data-id="element-816" />
              <span className="text-sm font-medium" data-id="element-817">{cat.name}</span>
            </button>)}
        </div>
        <div className="p-4 border-t border-neutral-200 bg-white" data-id="element-818">
          <Button fullWidth onClick={handleCompileAll} loading={isCompilingAll} className="bg-neutral-900 hover:bg-black text-white" data-id="element-819">
            Generate Evidence Pack
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-white" data-id="element-820">
        {/* Header */}
        <div className="p-6 border-b border-neutral-200 flex justify-between items-start" data-id="element-821">
          <div data-id="element-822">
            <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2" data-id="element-823">
              {categories.find(c => c.id === selectedCategory)?.name}
            </h1>
            <p className="text-neutral-500 mt-1" data-id="element-824">
              {categories.find(c => c.id === selectedCategory)?.description}
            </p>
          </div>
          <Button variant="outline" onClick={() => triggerAssembly(evidenceData[selectedCategory].map(i => i.id))} className="text-pink-600 border-pink-200 hover:bg-pink-50" data-id="element-825">
            <Sparkles size={16} data-id="element-826" /> Re-compile Category
          </Button>
        </div>

        {/* Evidence Grid */}
        <div className="flex-1 overflow-y-auto p-6 bg-neutral-50/50" data-id="element-827">
          <div className="grid grid-cols-1 gap-4" data-id="element-828">
            {currentItems.map(item => {
            const isAssembling = assemblingItems.has(item.id);
            return <div key={item.id} className="bg-white p-5 rounded-lg border border-neutral-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-829">
                  <div className="flex justify-between items-start" data-id="element-830">
                    <div className="flex-1" data-id="element-831">
                      <div className="flex items-center gap-2 mb-1" data-id="element-832">
                        <h3 className="font-semibold text-neutral-900" data-id="element-833">
                          {item.title}
                        </h3>
                        {isAssembling ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-100" data-id="element-834">
                            <Loader2 size={10} className="animate-spin" data-id="element-835" />
                            Assembling...
                          </span> : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-100 animate-in fade-in zoom-in duration-300" data-id="element-836">
                            <CheckCircle size={10} data-id="element-837" />
                            Complete
                          </span>}
                      </div>
                      <p className="text-sm text-neutral-500 mb-3" data-id="element-838">
                        {item.description}
                      </p>

                      <div className="flex items-center gap-3" data-id="element-839">
                        <button onClick={() => onNavigate?.(item.sourceDashboard)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-purple-50 text-purple-700 text-xs font-medium hover:bg-purple-100 transition-colors" data-id="element-840">
                          <Shield size={12} data-id="element-841" />
                          Source: {item.source}
                          <ExternalLink size={10} className="opacity-50" data-id="element-842" />
                        </button>
                        <span className="text-xs text-neutral-400" data-id="element-843">
                          Last compiled: {new Date().toLocaleTimeString()}
                        </span>
                      </div>
                    </div>

                    <div className="ml-6 pl-6 border-l border-neutral-100 min-w-[200px]" data-id="element-844">
                      <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-1" data-id="element-845">
                        Data Preview
                      </p>
                      {isAssembling ? <div className="h-6 w-32 bg-neutral-100 rounded animate-pulse" data-id="element-846" /> : <p className="text-sm font-mono font-medium text-neutral-900 animate-in fade-in slide-in-from-left-2 duration-300" data-id="element-847">
                          {item.dataPreview}
                        </p>}
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}